/**
 * One-time: data-end-seconds (store time from Liquid: fixed end date, or end = now + duration at render).
 * Recurring: IANA in data-tz-iana or CDabTime.DEFAULT_TZ; next end via Intl.
 */
(function () {
  'use strict';

  var CDabTime = {
    /** @type {string} Match store (recurring); not in theme schema. */
    DEFAULT_TZ: 'America/Toronto',
    WD: { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 },
    _ymd: new Map(),
    _hm: new Map(),
    _wk: new Map(),

    pad2: function (n) {
      return String(n).padStart(2, '0');
    },

    getYmdFormatter: function (iana) {
      if (!this._ymd.has(iana)) {
        this._ymd.set(
          iana,
          new Intl.DateTimeFormat('en-CA', {
            timeZone: iana,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })
        );
      }
      return this._ymd.get(iana);
    },

    getHmFormatter: function (iana) {
      if (!this._hm.has(iana)) {
        this._hm.set(
          iana,
          new Intl.DateTimeFormat('en-CA', {
            timeZone: iana,
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })
        );
      }
      return this._hm.get(iana);
    },

    getWkFormatter: function (iana) {
      if (!this._wk.has(iana)) {
        this._wk.set(iana, new Intl.DateTimeFormat('en-US', { timeZone: iana, weekday: 'long' }));
      }
      return this._wk.get(iana);
    },

    parseHm: function (s) {
      if (s == null || s === '') return { h: 0, m: 0 };
      var t = String(s).trim().split(':');
      var h = parseInt(t[0], 10);
      var m = t.length > 1 ? parseInt(t[1], 10) : 0;
      return { h: isNaN(h) ? 0 : h, m: isNaN(m) ? 0 : m };
    },

    ymdInIana: function (iana, ms) {
      try {
        var s = this.getYmdFormatter(iana).format(new Date(ms));
        var parts = s.split('-').map(Number);
        if (parts.length < 3 || isNaN(parts[0])) return null;
        return { y: parts[0], m: parts[1], d: parts[2] };
      } catch (e) {
        return null;
      }
    },

    hmInIana: function (iana, ms) {
      try {
        return this.getHmFormatter(iana).format(new Date(ms));
      } catch (e) {
        return null;
      }
    },

    getWeekday0Sun: function (iana, ms) {
      try {
        var w = this.getWkFormatter(iana).format(new Date(ms)).toLowerCase();
        return Object.prototype.hasOwnProperty.call(this.WD, w) ? this.WD[w] : 0;
      } catch (e) {
        return 0;
      }
    },

    getOffsetFormatter: function (iana) {
      if (!this._off) this._off = new Map();
      if (!this._off.has(iana)) {
        this._off.set(
          iana,
          new Intl.DateTimeFormat('en-US', {
            timeZone: iana,
            hourCycle: 'h23',
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
          })
        );
      }
      return this._off.get(iana);
    },

    /** Offset of `iana` from UTC at instant `ms`, in ms (wall clock minus UTC). */
    tzOffsetMs: function (iana, ms) {
      var parts = this.getOffsetFormatter(iana).formatToParts(new Date(ms));
      var v = {};
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].type !== 'literal') v[parts[i].type] = parseInt(parts[i].value, 10);
      }
      var wallAsUtc = Date.UTC(v.year, v.month - 1, v.day, v.hour, v.minute, v.second);
      return wallAsUtc - ((ms / 1000) | 0) * 1000;
    },

    /**
     * Instant for a wall-clock time in `iana`, computed from the zone offset (a handful of
     * Intl calls). Verified against the same formatters findIanaMsScan uses; if that check
     * fails (e.g. a DST gap) the original minute scan runs instead, so results are unchanged.
     */
    findIanaMs: function (iana, y, m, d, h, mi) {
      if (!iana) return null;
      try {
        var guess = Date.UTC(y, m - 1, d, h, mi, 0);
        var off1 = this.tzOffsetMs(iana, guess);
        var t = guess - off1;
        var off2 = this.tzOffsetMs(iana, t);
        if (off2 !== off1) t = guess - off2;

        var ymd = this.ymdInIana(iana, t);
        var hm = this.hmInIana(iana, t);
        if (ymd && hm && ymd.y === y && ymd.m === m && ymd.d === d) {
          var p = hm.split(':');
          if (parseInt(p[0], 10) === h && parseInt(p[1] || 0, 10) === mi) return t;
        }
      } catch (e) {
        /* fall through to the scan */
      }
      return this.findIanaMsScan(iana, y, m, d, h, mi);
    },

    findIanaMsScan: function (iana, y, m, d, h, mi) {
      if (!iana) return null;
      var t0 = Date.UTC(y, m - 1, d, 12, 0, 0) - 2 * 86400000;
      var t1 = t0 + 5 * 86400000;
      for (var ms = t0; ms <= t1; ms += 60000) {
        var a = this.ymdInIana(iana, ms);
        if (!a || a.y !== y || a.m !== m || a.d !== d) continue;
        var hms = this.hmInIana(iana, ms);
        if (!hms) continue;
        var p = hms.split(':');
        var ph = parseInt(p[0], 10);
        var pmi = parseInt(p[1] || 0, 10);
        if (ph === h && pmi === mi) return ms;
      }
      return null;
    },

    getNextRecurringEndMs: function (root, now) {
      var iana = (root.getAttribute('data-tz-iana') || this.DEFAULT_TZ).trim();
      if (!iana) return null;
      var tEnd = this.parseHm(root.getAttribute('data-cdab-end-hm') || '23:59');
      var h = tEnd.h;
      var m = tEnd.m;
      var kind = root.getAttribute('data-recur-kind') || 'daily';
      var recWd = 5;
      if (kind === 'weekly') {
        recWd = parseInt(root.getAttribute('data-recur-dow') || '5', 10);
        if (isNaN(recWd) || recWd < 0) recWd = 0;
        if (recWd > 6) recWd = 6;
      }
      var best = null;
      for (var d = 0; d < 45; d++) {
        var anchor = new Date(now + d * 864e5 + 18e6);
        var ymd = this.ymdInIana(iana, anchor.getTime());
        if (!ymd) continue;
        var inst = this.findIanaMs(iana, ymd.y, ymd.m, ymd.d, h, m);
        if (inst == null || inst <= now) continue;
        if (kind === 'weekly' && this.getWeekday0Sun(iana, inst) !== recWd) continue;
        if (best == null || inst < best) best = inst;
        // Later days can only give later instants, so the first match is the earliest.
        break;
      }
      return best;
    },
  };

  var CountdownAnnBar = /** @class */ (function () {
    function CountdownAnnBar(root) {
      this.root = root;
      this._recurEndMs = null;
    }

    CountdownAnnBar.prototype._readStaticAttrs = function () {
      this.cfmt = this.root.getAttribute('data-countdown-format') || 'dhms';
      this.isRec = this.root.getAttribute('data-recurring') === 'true';
      var es = this.root.getAttribute('data-end-seconds');
      this.endMs = this._secAttrToMs(es);
    };

    CountdownAnnBar.prototype._secAttrToMs = function (raw) {
      if (raw == null || raw === '') return null;
      var sec = parseInt(String(raw), 10);
      if (isNaN(sec) || sec <= 0) return null;
      return sec * 1000;
    };

    CountdownAnnBar.prototype._getEndMs = function (now) {
      if (this.isRec) {
        // connect() builds fresh instances for the same node (boot, then window load);
        // reuse the end time already worked out for it instead of recomputing.
        if (this._recurEndMs == null) this._recurEndMs = this.root._cdabRecurEndMs || null;
        if (this._recurEndMs == null || now >= this._recurEndMs) {
          var nxt = CDabTime.getNextRecurringEndMs(this.root, now);
          this._recurEndMs = nxt != null ? nxt : now + 864e5;
          this.root._cdabRecurEndMs = this._recurEndMs;
        }
        return this._recurEndMs;
      }
      return this.endMs;
    };

    CountdownAnnBar.prototype._updateUnits = function (el, remSec) {
      var cfmt = this.cfmt;
      var p2 = CDabTime.pad2;
      var u = el.getElementsByClassName('js-cad-unit');
      var n = u.length;
      if (cfmt === 'hms' && n >= 3) {
        var h = (remSec / 3600) | 0;
        var r2 = remSec - h * 3600;
        var mo = (r2 / 60) | 0;
        var s2 = r2 - mo * 60;
        u[0].textContent = h > 99 ? String(h) : p2(h);
        u[1].textContent = p2(mo);
        u[2].textContent = p2(s2);
        return;
      }
      if (n >= 4) {
        var days = (remSec / 86400) | 0;
        var rem2 = remSec - days * 86400;
        var hours = (rem2 / 3600) | 0;
        rem2 -= hours * 3600;
        var mins = (rem2 / 60) | 0;
        var secs = rem2 - mins * 60;
        u[0].textContent = p2(days);
        u[1].textContent = p2(hours);
        u[2].textContent = p2(mins);
        u[3].textContent = p2(secs);
      }
    };

    CountdownAnnBar.prototype.update = function (now) {
      this._readStaticAttrs();
      var endMs = this._getEndMs(now);
      if (endMs == null) return;
      var annEls = this.root.querySelectorAll('.js-countdown-ann');
      if (!annEls.length) return;
      var rem = ((endMs - now) * 0.001) | 0;
      if (rem < 0) rem = 0;
      var i;
      for (i = 0; i < annEls.length; i++) {
        this._updateUnits(annEls[i], rem);
      }
    };

    return CountdownAnnBar;
  })();

  /**
   * Theme only auto-inits new theme.Disclosure on #FooterLocale (footer). The countdown bar
   * uses its own ids (FooterLocaleCDAB-…), so we must bind the same component here.
   */
  var CdabLangDisclosure = {
    _list: null,
    _resizeT: 0,

    /**
     * On mobile, theme.liquid moveForm() moves the form to .move-selector-here in the nav drawer
     * *before* deferred scripts run — so the .disclosure is not under .absolute-language-selector.
     * `form[data-cdab-locale-form] .disclosure` matches in the bar (desktop) and in the drawer.
     */
    init: function () {
      CdabLangDisclosure.destroy();
      if (typeof window.theme === 'undefined' || !theme.Disclosure) return;
      var nodes = document.querySelectorAll('form[data-cdab-locale-form] .disclosure');
      if (!nodes.length) return;
      this._list = [];
      for (var i = 0; i < nodes.length; i++) {
        try {
          this._list.push(new theme.Disclosure(nodes[i]));
        } catch (e) {
          /* no-op */
        }
      }
    },

    destroy: function () {
      if (!this._list) return;
      for (var i = 0; i < this._list.length; i++) {
        try {
          this._list[i].destroy();
        } catch (e) {
          /* no-op */
        }
      }
      this._list = null;
    },
  };

  var CdabManager = {
    _bars: [],
    _id: 0,
    _selector: '[data-countdown-annbar]',

    connect: function () {
      this.disconnect();
      var nodes = document.querySelectorAll(this._selector);
      var i;
      for (i = 0; i < nodes.length; i++) {
        this._bars.push(new CountdownAnnBar(nodes[i]));
      }
    },

    disconnect: function () {
      this._bars.length = 0;
    },

    tick: function () {
      var t = Date.now();
      var i;
      for (i = 0; i < this._bars.length; i++) {
        this._bars[i].update(t);
      }
    },

    onSectionLoad: function () {
      CdabManager.connect();
      CdabManager.tick();
      CdabLangDisclosure.init();
    },

    start: function () {
      CdabManager.connect();
      CdabManager.tick();
      if (!this._id) {
        this._id = window.setInterval(function () {
          CdabManager.tick();
        }, 1000);
      }
    },
  };

  function boot() {
    CdabManager.start();
    CdabLangDisclosure.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
  window.addEventListener('load', function () {
    CdabManager.onSectionLoad();
  });
  document.addEventListener('shopify:section:load', function () {
    CdabManager.onSectionLoad();
  });
  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('.cdab-countdown-ann')) {
      CdabLangDisclosure.destroy();
    }
  });
  function scheduleCdabLangInitAfterLayout() {
    clearTimeout(CdabLangDisclosure._resizeT);
    CdabLangDisclosure._resizeT = setTimeout(function () {
      CdabLangDisclosure.init();
    }, 150);
  }
  window.addEventListener('resize', scheduleCdabLangInitAfterLayout);
})();
